import { useState, useCallback } from 'react';
import { buildWebDataForProposal } from '../parser/buildWebDataForProposal';
import { fetchWebDataWithVersion, saveWebData } from '../requests/webProposal';
import { webEditorPath } from '../utils/webRoutes';
import { isStyleMappingCancelled, useStyleMappingPrompt } from '../components/StyleMapping/StyleMappingModal';

/**
 * Hook qui orchestre la génération du site web depuis un proposal.
 *
 * Étapes :
 *  1. buildWebDataForProposal(...)  → proposal + template + variables, puis parse
 *     (s'arrête sur `modal` si des styles du document sont à faire correspondre)
 *  2. POST /api/proposal/:id/web_data → sauvegarder
 *  3. Redirect → /web-editor/:proposalId
 *
 * Le jeton de version est **obligatoire**, même pour la toute première
 * écriture : `web_data_version` vaut 0 par défaut en base et jamais NULL, et le
 * serveur compare `current.to_s == expected.to_s` (`Proposals::WebWriter`). Ne
 * rien envoyer, c'est comparer « 0 » à « », donc se faire refuser en 409
 * `stale_version` à tous les coups. Même piège que celui déjà documenté pour
 * les scopes dans `autosave/webDraftPersistence.js`.
 */
const useWebGeneration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { chooseStyleMapping, modal } = useStyleMappingPrompt();

  const generate = useCallback(async (proposalId, templateId, documentIdentity = null) => {
    setLoading(true);
    setError(null);
    try {
      // 1. Charger + parser (pages statiques du template + pages proposal + pageOrder déjà ordonnés)
      const webData = await buildWebDataForProposal({
        proposalId,
        templateId,
        documentIdentity,
        chooseStyleMapping,
      });

      // 2. Sauvegarder, avec le jeton que le serveur porte aujourd'hui
      const { version } = await fetchWebDataWithVersion(proposalId);
      await saveWebData(proposalId, webData, version ?? 0);

      // 3. Rediriger vers l'éditeur
      window.location.href = webEditorPath(proposalId, documentIdentity);
    } catch (e) {
      // Annulé dans la modale des styles : rien n'est écrit, pas d'erreur à dire.
      if (!isStyleMappingCancelled(e)) setError(e.message);
      setLoading(false);
    }
  }, [chooseStyleMapping]);

  // `modal` : correspondance des styles, à rendre par l'appelant.
  return {
    generate, loading, error, modal,
  };
};

export default useWebGeneration;
